import request from '../../utils/request';

export function getPartners(params) {
  return request('/partners', {
    method: 'GET',
    params: params,
  });
}

export function getPartner(id) {
  return request(`/partners/${id}`, {
    method: 'GET',
  });
}

export function createPartner(data) {
  return request('/partners', {
    method: 'POST',
    data,
  });
}

export function updatePartner(data) {
  const id = data.id;
  delete data.id;
  return request(`/partners/${id}`, {
    method: 'PATCH',
    data,
  });
}

export function removePartner(id) {
  return request(`/partners/${id}`, {
    method: 'DELETE',
  });
}

// Partner Route History

export function getPartnerRoutes(partnerId, params) {
  return request(`/partners/${partnerId}/routes`, {
    method: 'GET',
    params,
  });
}

// Partner Item Types (nested under partner)

export function getPartnerItemTypes(partnerId) {
  return request(`/partners/${partnerId}/partner-item-types`, {
    method: 'GET',
  });
}

export function createPartnerItemType(partnerId, data) {
  return request(`/partners/${partnerId}/partner-item-types`, {
    method: 'POST',
    data,
  });
}

export function updatePartnerItemType(data) {
  const id = data.id;
  delete data.id;
  return request(`/partner-item-types/${id}`, {
    method: 'PATCH',
    data,
  });
}

export function removePartnerItemType(id) {
  return request(`/partner-item-types/${id}`, {
    method: 'DELETE',
  });
}

// Partner Items (individual trackable items under a type)

export function getPartnerItems(typeId, params) {
  return request(`/partner-item-types/${typeId}/partner-items`, {
    method: 'GET',
    params,
  });
}

export function createPartnerItem(typeId, data) {
  return request(`/partner-item-types/${typeId}/partner-items`, {
    method: 'POST',
    data,
  });
}

export function updatePartnerItem(data) {
  const id = data.id;
  delete data.id;
  return request(`/partner-items/${id}`, {
    method: 'PATCH',
    data,
  });
}

export function removePartnerItem(id) {
  return request(`/partner-items/${id}`, {
    method: 'DELETE',
  });
}

export function bulkCreatePartnerItems(typeId, serialNumbers) {
  return request(`/partner-item-types/${typeId}/partner-items/bulk`, {
    method: 'POST',
    data: { serialNumbers },
  });
}

// Partner Viewers (partner_viewer users)

export function getPartnerViewers(partnerId, params) {
  return request(`/partners/${partnerId}/viewers`, {
    method: 'GET',
    params,
  });
}

export function createPartnerViewer(partnerId, data) {
  return request(`/partners/${partnerId}/viewers`, {
    method: 'POST',
    data,
  });
}

export function updatePartnerViewer(partnerId, viewerId, data) {
  return request(`/partners/${partnerId}/viewers/${viewerId}`, {
    method: 'PATCH',
    data,
  });
}

export function removePartnerViewer(partnerId, viewerId) {
  return request(`/partners/${partnerId}/viewers/${viewerId}`, {
    method: 'DELETE',
  });
}

// Partner Viewer (read-only, scoped to authenticated user's partner)

export function getMyPartner() {
  return request('/partners/me', { method: 'GET' });
}

export function getMyPartnerItemTypes() {
  return request('/partners/me/item-types', { method: 'GET' });
}

export function getMyPartnerViewers() {
  return request('/partners/me/viewers', { method: 'GET' });
}

export function getMyPartnerRoutes(params) {
  return request('/partners/me/routes', { method: 'GET', params });
}

// OVO-002 — partner-scoped single-route detail. Returns only this
// partner's stops, renumbered relative to their slice.
export function getMyPartnerRoute(id) {
  return request(`/partners/me/routes/${id}`, { method: 'GET' });
}

// Partner Stats
// `query` is { period, from, to, groupBy } — all optional. Custom range
// (from + to) wins over the preset; `groupBy` is 'pickupName' | 'deliveryName'
// to populate the per-name breakdown table.

function statsParams(query) {
  const params = {};
  if (query?.period) params.period = query.period;
  if (query?.from) params.from = query.from;
  if (query?.to) params.to = query.to;
  if (query?.groupBy) params.groupBy = query.groupBy;
  return params;
}

export function getPartnerStats(partnerId, query) {
  return request(`/partners/${partnerId}/stats`, {
    method: 'GET',
    params: statsParams(query),
  });
}

export function getMyPartnerStats(query) {
  return request('/partners/me/stats', {
    method: 'GET',
    params: statsParams(query),
  });
}

export function getGlobalStats(query, partnerIds, fleetTags, driverIds) {
  const params = statsParams(query);
  if (partnerIds && partnerIds.length > 0) {
    params.partnerIds = partnerIds.join(',');
  }
  // OVO-221 — vehicle-type + driver filters (scope the whole stats page).
  if (fleetTags && fleetTags.length > 0) {
    params.fleetTags = fleetTags.join(',');
  }
  if (driverIds && driverIds.length > 0) {
    params.driverIds = driverIds.join(',');
  }
  return request('/partners/global/stats', {
    method: 'GET',
    params,
  });
}

// OVO-248 — the routes that sum into a stats KPI. Same params as
// getGlobalStats, so the popup shows exactly what the card counted.
export function getStatsRouteBreakdown({ query, partnerIds, fleetTags, driverIds } = {}) {
  const params = statsParams(query);
  if (partnerIds && partnerIds.length > 0) params.partnerIds = partnerIds.join(',');
  if (fleetTags && fleetTags.length > 0) params.fleetTags = fleetTags.join(',');
  if (driverIds && driverIds.length > 0) params.driverIds = driverIds.join(',');
  return request('/partners/global/stats/routes', { method: 'GET', params });
}

// CO2 comparison baseline (g CO2-eq/km) — editable global setting (Felipe,
// Jun 2026). Applies to every "saved vs car" figure. Admin only.
export function setCo2Baseline(value) {
  return request('/settings/co2-baseline', {
    method: 'PATCH',
    data: { value },
  });
}

// Webhook Logs

export function getOrderWebhookLogs(orderCode) {
  return request(`/orders/${orderCode}/webhook-logs`, {
    method: 'GET',
  });
}

export function retryWebhookLog(orderCode, logId) {
  return request(`/orders/${orderCode}/webhook-logs/${logId}/retry`, {
    method: 'POST',
  });
}

// Collected Items

export function getPartnerCollectedItems(partnerId, params = {}) {
  return request(`/partners/${partnerId}/collected-items`, {
    method: 'GET',
    params,
  });
}

// OVO-151 — partner-viewer variant: own partner's collected EMPTIES,
// scoped server-side by the JWT's partnerId; no driver identity.
export function getMyPartnerCollectedItems(params = {}) {
  return request('/partners/me/collected-items', {
    method: 'GET',
    params,
  });
}

// OVO-019 — global list of partner item types across all partners.
// Used by the OrdersFilter "Product Type" multi-select.
export function getAllPartnerItemTypes() {
  return request('/partner-item-types', { method: 'GET' });
}
